import { makeAutoObservable, runInAction } from 'mobx';

interface UnitCategory {
  id: string;
  name: string;
  units: Unit[];
}

interface Unit {
  id: string;
  name: string;
  symbol: string;
  conversionFactor: number; // Factor to convert to base unit
  isBase?: boolean;
}

interface ConversionHistory {
  id: string;
  fromValue: number;
  fromUnit: Unit;
  toValue: number;
  toUnit: Unit;
  category: string;
  timestamp: number;
}

class UnitConverterStore {
  private _categories: UnitCategory[] = [];
  private _selectedCategoryId: string = 'length';
  private _fromUnitId: string = '';
  private _toUnitId: string = '';
  private _inputValue: string = '1';
  private _result: number = 0;
  private _history: ConversionHistory[] = [];

  constructor() {
    makeAutoObservable(this);
    this.initializeCategories();
    this.loadHistory();
    this.setDefaultUnits();
  }

  get categories(): UnitCategory[] {
    return this._categories;
  }

  get selectedCategory(): UnitCategory | null {
    return this._categories.find(cat => cat.id === this._selectedCategoryId) || null;
  }

  get selectedCategoryId(): string {
    return this._selectedCategoryId;
  }

  get fromUnit(): Unit | null {
    if (!this.selectedCategory) return null;
    return this.selectedCategory.units.find(unit => unit.id === this._fromUnitId) || null;
  }

  get toUnit(): Unit | null {
    if (!this.selectedCategory) return null;
    return this.selectedCategory.units.find(unit => unit.id === this._toUnitId) || null;
  }

  get inputValue(): string {
    return this._inputValue;
  }

  get result(): number {
    return this._result;
  }

  get formattedResult(): string {
    if (this._result === 0) return '0';
    
    // Use appropriate precision based on the magnitude
    if (Math.abs(this._result) >= 1000000) {
      return this._result.toExponential(6);
    } else if (Math.abs(this._result) < 0.001) {
      return this._result.toExponential(6);
    } else {
      return this._result.toFixed(8).replace(/\.?0+$/, '');
    }
  }

  get history(): ConversionHistory[] {
    return this._history.slice().reverse(); // Most recent first
  }

  selectCategory(categoryId: string): void {
    this._selectedCategoryId = categoryId;
    this.setDefaultUnits();
    this.performConversion();
  }

  setFromUnit(unitId: string): void {
    this._fromUnitId = unitId;
    this.performConversion();
  }

  setToUnit(unitId: string): void {
    this._toUnitId = unitId;
    this.performConversion();
  }

  setInputValue(value: string): void {
    this._inputValue = value;
    this.performConversion();
  }

  swapUnits(): void {
    const tempFromUnit = this._fromUnitId;
    const tempToUnit = this._toUnitId;
    
    this._fromUnitId = tempToUnit;
    this._toUnitId = tempFromUnit;
    this._inputValue = this.formattedResult;
    
    this.performConversion();
  }

  clearHistory(): void {
    this._history = [];
    this.saveHistory();
  }

  deleteHistoryItem(id: string): void {
    this._history = this._history.filter(item => item.id !== id);
    this.saveHistory();
  }

  useHistoryConversion(historyItem: ConversionHistory): void {
    const category = this._categories.find(cat => cat.name === historyItem.category);
    if (!category) return;

    this._selectedCategoryId = category.id;
    this._fromUnitId = historyItem.fromUnit.id;
    this._toUnitId = historyItem.toUnit.id;
    this._inputValue = historyItem.fromValue.toString();
    this.performConversion();
  }

  private performConversion(): void {
    const inputNum = parseFloat(this._inputValue);
    
    if (isNaN(inputNum) || !this.fromUnit || !this.toUnit) {
      this._result = 0;
      return;
    }

    let result: number;
    
    // Special handling for temperature conversions
    if (this.selectedCategory?.id === 'temperature') {
      result = this.convertTemperature(inputNum, this.fromUnit, this.toUnit);
    } else {
      // Convert from input unit to base unit, then to target unit
      const baseValue = inputNum * this.fromUnit.conversionFactor;
      result = baseValue / this.toUnit.conversionFactor;
    }
    
    runInAction(() => {
      this._result = result;
    });

    // Add to history if conversion is valid and not zero
    if (inputNum !== 0 && this.fromUnit && this.toUnit && this.selectedCategory) {
      this.addToHistory(inputNum, this.fromUnit, result, this.toUnit, this.selectedCategory.name);
    }
  }

  private addToHistory(fromValue: number, fromUnit: Unit, toValue: number, toUnit: Unit, category: string): void {
    // Don't add duplicate consecutive conversions
    const lastItem = this._history[this._history.length - 1];
    if (lastItem && 
        lastItem.fromValue === fromValue && 
        lastItem.fromUnit.id === fromUnit.id && 
        lastItem.toUnit.id === toUnit.id) {
      return;
    }

    const historyItem: ConversionHistory = {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      fromValue,
      fromUnit: { ...fromUnit },
      toValue,
      toUnit: { ...toUnit },
      category,
      timestamp: Date.now()
    };

    this._history.push(historyItem);
    
    // Keep only last 50 items
    if (this._history.length > 50) {
      this._history = this._history.slice(-50);
    }

    this.saveHistory();
  }

  private setDefaultUnits(): void {
    if (!this.selectedCategory || this.selectedCategory.units.length === 0) return;
    
    const units = this.selectedCategory.units;
    this._fromUnitId = units[0].id;
    this._toUnitId = units.length > 1 ? units[1].id : units[0].id;
  }

  private initializeCategories(): void {
    this._categories = [
      {
        id: 'length',
        name: 'Length',
        units: [
          { id: 'mm', name: 'Millimeter', symbol: 'mm', conversionFactor: 0.001 },
          { id: 'cm', name: 'Centimeter', symbol: 'cm', conversionFactor: 0.01 },
          { id: 'm', name: 'Meter', symbol: 'm', conversionFactor: 1, isBase: true },
          { id: 'km', name: 'Kilometer', symbol: 'km', conversionFactor: 1000 },
          { id: 'in', name: 'Inch', symbol: 'in', conversionFactor: 0.0254 },
          { id: 'ft', name: 'Foot', symbol: 'ft', conversionFactor: 0.3048 },
          { id: 'yd', name: 'Yard', symbol: 'yd', conversionFactor: 0.9144 },
          { id: 'mile', name: 'Mile', symbol: 'mi', conversionFactor: 1609.344 },
          { id: 'nmi', name: 'Nautical Mile', symbol: 'nmi', conversionFactor: 1852 }
        ]
      },
      {
        id: 'weight',
        name: 'Weight',
        units: [
          { id: 'mg', name: 'Milligram', symbol: 'mg', conversionFactor: 0.000001 },
          { id: 'g', name: 'Gram', symbol: 'g', conversionFactor: 0.001 },
          { id: 'kg', name: 'Kilogram', symbol: 'kg', conversionFactor: 1, isBase: true },
          { id: 't', name: 'Metric Ton', symbol: 't', conversionFactor: 1000 },
          { id: 'oz', name: 'Ounce', symbol: 'oz', conversionFactor: 0.0283495 },
          { id: 'lb', name: 'Pound', symbol: 'lb', conversionFactor: 0.453592 },
          { id: 'stone', name: 'Stone', symbol: 'st', conversionFactor: 6.35029 }
        ]
      },
      {
        id: 'temperature',
        name: 'Temperature',
        units: [
          { id: 'c', name: 'Celsius', symbol: '°C', conversionFactor: 1, isBase: true },
          { id: 'f', name: 'Fahrenheit', symbol: '°F', conversionFactor: 1 },
          { id: 'k', name: 'Kelvin', symbol: 'K', conversionFactor: 1 }
        ]
      },
      {
        id: 'area',
        name: 'Area',
        units: [
          { id: 'mm2', name: 'Square Millimeter', symbol: 'mm²', conversionFactor: 0.000001 },
          { id: 'cm2', name: 'Square Centimeter', symbol: 'cm²', conversionFactor: 0.0001 },
          { id: 'm2', name: 'Square Meter', symbol: 'm²', conversionFactor: 1, isBase: true },
          { id: 'km2', name: 'Square Kilometer', symbol: 'km²', conversionFactor: 1000000 },
          { id: 'ha', name: 'Hectare', symbol: 'ha', conversionFactor: 10000 },
          { id: 'in2', name: 'Square Inch', symbol: 'in²', conversionFactor: 0.00064516 },
          { id: 'ft2', name: 'Square Foot', symbol: 'ft²', conversionFactor: 0.092903 },
          { id: 'ac', name: 'Acre', symbol: 'ac', conversionFactor: 4046.86 }
        ]
      },
      {
        id: 'volume',
        name: 'Volume',
        units: [
          { id: 'ml', name: 'Milliliter', symbol: 'ml', conversionFactor: 0.001 },
          { id: 'l', name: 'Liter', symbol: 'l', conversionFactor: 1, isBase: true },
          { id: 'm3', name: 'Cubic Meter', symbol: 'm³', conversionFactor: 1000 },
          { id: 'fl_oz', name: 'Fluid Ounce (US)', symbol: 'fl oz', conversionFactor: 0.0295735 },
          { id: 'cup', name: 'Cup (US)', symbol: 'cup', conversionFactor: 0.236588 },
          { id: 'pt', name: 'Pint (US)', symbol: 'pt', conversionFactor: 0.473176 },
          { id: 'qt', name: 'Quart (US)', symbol: 'qt', conversionFactor: 0.946353 },
          { id: 'gal', name: 'Gallon (US)', symbol: 'gal', conversionFactor: 3.78541 }
        ]
      },
      {
        id: 'speed',
        name: 'Speed',
        units: [
          { id: 'ms', name: 'Meter per Second', symbol: 'm/s', conversionFactor: 1, isBase: true },
          { id: 'kmh', name: 'Kilometer per Hour', symbol: 'km/h', conversionFactor: 0.277778 },
          { id: 'mph', name: 'Mile per Hour', symbol: 'mph', conversionFactor: 0.44704 },
          { id: 'fps', name: 'Foot per Second', symbol: 'ft/s', conversionFactor: 0.3048 },
          { id: 'knot', name: 'Knot', symbol: 'kn', conversionFactor: 0.514444 }
        ]
      },
      {
        id: 'energy',
        name: 'Energy',
        units: [
          { id: 'j', name: 'Joule', symbol: 'J', conversionFactor: 1, isBase: true },
          { id: 'kj', name: 'Kilojoule', symbol: 'kJ', conversionFactor: 1000 },
          { id: 'cal', name: 'Calorie', symbol: 'cal', conversionFactor: 4.184 },
          { id: 'kcal', name: 'Kilocalorie', symbol: 'kcal', conversionFactor: 4184 },
          { id: 'wh', name: 'Watt Hour', symbol: 'Wh', conversionFactor: 3600 },
          { id: 'kwh', name: 'Kilowatt Hour', symbol: 'kWh', conversionFactor: 3600000 },
          { id: 'btu', name: 'British Thermal Unit', symbol: 'BTU', conversionFactor: 1055.06 }
        ]
      },
      {
        id: 'time',
        name: 'Time',
        units: [
          { id: 'ms', name: 'Millisecond', symbol: 'ms', conversionFactor: 0.001 },
          { id: 's', name: 'Second', symbol: 's', conversionFactor: 1, isBase: true },
          { id: 'min', name: 'Minute', symbol: 'min', conversionFactor: 60 },
          { id: 'h', name: 'Hour', symbol: 'h', conversionFactor: 3600 },
          { id: 'd', name: 'Day', symbol: 'd', conversionFactor: 86400 },
          { id: 'week', name: 'Week', symbol: 'wk', conversionFactor: 604800 },
          { id: 'month', name: 'Month (30 days)', symbol: 'mo', conversionFactor: 2592000 },
          { id: 'year', name: 'Year (365 days)', symbol: 'yr', conversionFactor: 31536000 }
        ]
      }
    ];
  }

  private saveHistory(): void {
    try {
      localStorage.setItem('general-tools-unit-converter-history', JSON.stringify(this._history));
    } catch (error) {
      console.error('Failed to save unit converter history:', error);
    }
  }

  private loadHistory(): void {
    try {
      const saved = localStorage.getItem('general-tools-unit-converter-history');
      if (saved) {
        this._history = JSON.parse(saved);
      }
    } catch (error) {
      console.error('Failed to load unit converter history:', error);
      this._history = [];
    }
  }

  // Special handling for temperature conversions
  private convertTemperature(value: number, fromUnit: Unit, toUnit: Unit): number {
    if (fromUnit.id === toUnit.id) return value;

    // Convert to Celsius first
    let celsius: number;
    switch (fromUnit.id) {
      case 'c':
        celsius = value;
        break;
      case 'f':
        celsius = (value - 32) * 5/9;
        break;
      case 'k':
        celsius = value - 273.15;
        break;
      default:
        return value;
    }

    // Convert from Celsius to target
    switch (toUnit.id) {
      case 'c':
        return celsius;
      case 'f':
        return celsius * 9/5 + 32;
      case 'k':
        return celsius + 273.15;
      default:
        return value;
    }
  }
}

export const unitConverterStore = new UnitConverterStore();
